import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import { useLocale } from "@genius-tracker/i18n/react";
import { PINNED_ITEMS, NAV_GROUPS, FOOTER_ITEMS, type NavGroup, type NavItem } from "./navModel.js";
import { ALL_MODULES_ACTIVE, type ModuleView } from "../modules/useModules.js";
import { BrandLogo } from "./BrandLogo.js";
import { LockIcon } from "./NavIcons.js";
import { prefetchRoute } from "../routePrefetch.js";

/**
 * GT-NAV-IA-DOMAIN-GROUPING-FUEL-COSTS-GENIUS-FAMILY-01 (DL-124) — the App Shell
 * domain-based navigation (desktop sidebar + the body of the mobile drawer).
 *
 * Structure (single source of truth: `navModel.ts`):
 *   - brand (approved logo, `BrandLogo`),
 *   - **pinned** items (Dashboard · Genius Intelligence · Live Map · Trips)
 *     — always visible, never inside an accordion,
 *   - **domain groups** as accessible **accordions** (Genius / Fleet / Monitoring /
 *     Fuel & Costs / Maintenance / Reports / Integrations / Administration); the group
 *     that owns the active route opens automatically,
 *   - a **footer** with standalone links for all roles (Support).
 *
 * Desktop only: `collapsed` turns the sidebar into an icon rail (72 px). The domain
 * groups then show their icons flat (each icon keeps its name as tooltip and
 * accessible name), and the mark replaces the wordmark. The toggle sits at the foot
 * of the sidebar in both states, as one element whose icon flips. The mobile drawer
 * never passes `collapsed`, so it always shows the full menu and no toggle.
 *
 * Both the desktop `<aside>` and the mobile drawer render this one component so the two
 * surfaces can never drift. Every prior route stays reachable; future capabilities are
 * honest, badged **preview** items (no broken links), and the Platform Admin Console is
 * an honest external out-link.
 *
 * Accessibility: a `<nav>` landmark, `aria-current="page"` on the active item (via
 * `NavLink`), group disclosures are real `<button>`s with `aria-expanded` /
 * `aria-controls`, text labels on every item (collapsed: `aria-label` + `title`), and a
 * visible focus ring from the global styles. Theming: only `--gt-sys-*` tokens.
 */
export function AppSidebar({
  showAdmin = false,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
  modules = ALL_MODULES_ACTIVE,
}: {
  /**
   * A-W4-WEB1 (W4 Schritt 4) — darf die Administration im Menue erscheinen?
   * Bis W4 kam hier die Rollenliste herein und die Navigation entschied selbst
   * (`hasAdminRole`). Die Entscheidung faellt jetzt in der Shell auf der
   * gerechneten Rechtemenge aus `GET /v1/me/access`; hier kommt nur noch das
   * Ergebnis an. Vorbelegung `false` ist die fail-closed-Richtung: ohne Aussage
   * fehlt die Administration, statt sie jemandem zu zeigen, den der Server
   * danach mit 403 abweist.
   */
  readonly showAdmin?: boolean;
  /** Called after a nav item is activated (used to close the mobile drawer). */
  readonly onNavigate?: () => void;
  /** Desktop icon rail. Omitted by the mobile drawer, which always shows the full menu. */
  readonly collapsed?: boolean;
  /** Shows the collapse/expand toggle at the foot of the sidebar (desktop only). */
  readonly onToggleCollapsed?: () => void;
  /**
   * A-MODUL-NAV1 — Modulsicht aus der Shell (`useModules`). Vorbelegung "alles
   * aktiv": ohne Shell-Kontext zeigt die Navigation vollstaendig, nie leer.
   * Ausgeblendete Module verschwinden, `read_only` wird nur gekennzeichnet —
   * die Schreibsperren macht der Server (Lane C).
   */
  readonly modules?: ModuleView;
}): ReactNode {
  const { t } = useLocale();
  const { pathname } = useLocation();
  const activeGroup = activeGroupId(pathname);
  const isCollapsed = collapsed && onToggleCollapsed !== undefined;

  // A-MODUL-NAV1 — Menuefilter an EINER Stelle. Die Zuordnung Menuepunkt ->
  // Modul steht in `modules/moduleMap.ts`; Basis-Punkte (Flotte, Karte,
  // Fahrten, Uebersicht) kommen dort immer als aktiv zurueck und bleiben
  // damit sichtbar. Eine Gruppe, die dadurch leer wird, verschwindet samt
  // Kopfzeile — ein leerer Aufklapper waere eine Sackgasse.
  const visibleItems = (items: ReadonlyArray<NavItem>): ReadonlyArray<NavItem> =>
    items.filter((item) => !modules.isNavItemHidden(item));
  const pinnedItems = visibleItems(PINNED_ITEMS);
  const footerItems = visibleItems(FOOTER_ITEMS);

  // Accordion state: the group that owns the active route is open by default; the user
  // can open/close any group. Navigating into a group's route opens that group (it does
  // not force the others closed). In-memory only — persistence is a documented future.
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(
    () => new Set(activeGroup ? [activeGroup] : []),
  );
  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((prev) => {
      if (prev.has(activeGroup)) return prev;
      const next = new Set(prev);
      next.add(activeGroup);
      return next;
    });
  }, [activeGroup]);

  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <nav
      aria-label={t("nav.sidebar.aria")}
      data-testid="app-sidebar"
      data-collapsed={isCollapsed ? "true" : undefined}
      style={isCollapsed ? navCollapsedStyle : navStyle}
    >
      <NavLink
        to="/"
        end
        aria-label={t("app.title")}
        onClick={onNavigate}
        style={brandStyle}
        data-testid="app-sidebar-brand"
      >
        {isCollapsed ? (
          <BrandLogo height={32} variant="mark" />
        ) : (
          <BrandLogo height={44} variant="wordmark" />
        )}
      </NavLink>

      {/* Scroll region: pinned items and domain groups. Only this list scrolls, so the
          footer and its collapse toggle keep the same position in both states. */}
      <div data-testid="app-sidebar-scroll" style={scrollRegionStyle}>
        {/* Pinned daily-cockpit items — always visible, never inside an accordion. */}
        <ul role="list" data-testid="app-sidebar-pinned" style={listStyle}>
          {pinnedItems.map((item) => (
            <li key={item.id}>
              <SidebarItem
                item={item}
                collapsed={isCollapsed}
                onNavigate={onNavigate}
                readOnly={modules.isNavItemReadOnly(item)}
              />
            </li>
          ))}
        </ul>

        {/* Domain groups: accordions when expanded, a flat icon list when collapsed. */}
        {NAV_GROUPS.map((group) => {
          if (group.adminOnly && !showAdmin) return null;
          const items = visibleItems(group.items);
          if (items.length === 0) return null;
          const shown: NavGroup = { ...group, items };
          if (isCollapsed) {
            return (
              <CollapsedGroup
                key={group.id}
                group={shown}
                onNavigate={onNavigate}
                modules={modules}
              />
            );
          }
          return (
            <SidebarAccordionGroup
              key={group.id}
              group={shown}
              open={openGroups.has(group.id)}
              onToggle={() => toggleGroup(group.id)}
              onNavigate={onNavigate}
              modules={modules}
            />
          );
        })}
      </div>

      {/* Footer — standalone links for all roles, then the collapse toggle (fixed at the
          bottom of the sidebar in both states). */}
      <div style={footerStyle}>
        <ul role="list" data-testid="app-sidebar-footer" style={listStyle}>
          {footerItems
            .filter((item) => !item.badgeImage || isCollapsed)
            .map((item) => (
              <li key={item.id}>
                <SidebarItem item={item} collapsed={isCollapsed} onNavigate={onNavigate} />
              </li>
            ))}
          {!isCollapsed && footerItems.some((item) => item.badgeImage) ? (
            <li data-testid="app-sidebar-store-badges" style={storeBadgeRowStyle}>
              {footerItems
                .filter((item) => item.badgeImage)
                .map((item) => (
                  <span key={item.id} style={storeBadgeSlotStyle}>
                    <SidebarItem item={item} collapsed={false} onNavigate={onNavigate} />
                  </span>
                ))}
            </li>
          ) : null}
        </ul>
        {onToggleCollapsed ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? t("nav.sidebar.expand") : t("nav.sidebar.collapse")}
            title={isCollapsed ? t("nav.sidebar.expand") : t("nav.sidebar.collapse")}
            data-testid="app-sidebar-collapse-toggle"
            style={isCollapsed ? collapseToggleCollapsedStyle : collapseToggleStyle}
          >
            <CollapseChevrons flipped={isCollapsed} />
            {isCollapsed ? null : <span style={labelSlotStyle}>{t("nav.sidebar.collapse")}</span>}
          </button>
        ) : null}
      </div>
    </nav>
  );
}

/**
 * A domain group rendered as an accessible disclosure (accordion). The header is a real
 * `<button>` with `aria-expanded` / `aria-controls`; its accessible name is the group
 * label (the chevron is `aria-hidden`). Collapsed groups do not render their items
 * (standard disclosure) — the active group opens by default so the active item shows.
 */
function SidebarAccordionGroup({
  group,
  open,
  onToggle,
  onNavigate,
  modules = ALL_MODULES_ACTIVE,
}: {
  group: NavGroup;
  open: boolean;
  onToggle: () => void;
  onNavigate?: (() => void) | undefined;
  modules?: ModuleView;
}) {
  const { t } = useLocale();
  const regionId = `app-sidebar-group-${group.id}-region`;
  return (
    <div data-testid={`app-sidebar-group-${group.id}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={regionId}
        data-testid={`app-sidebar-group-${group.id}-toggle`}
        style={groupToggleStyle}
      >
        <span style={groupHeadingStyle}>
          <span
            aria-hidden
            style={iconSlotStyle}
            data-testid={`app-sidebar-group-${group.id}-icon`}
          >
            <group.Icon />
          </span>
          <span style={groupLabelStyle}>{t(group.labelKey)}</span>
        </span>
        <span
          aria-hidden="true"
          style={{ ...chevronStyle, transform: open ? "rotate(180deg)" : "none" }}
        >
          ▾
        </span>
      </button>
      {open ? (
        <ul role="list" id={regionId} style={groupListStyle}>
          {group.items.map((item) => (
            <li key={item.id}>
              <SidebarItem
                item={item}
                onNavigate={onNavigate}
                readOnly={modules.isNavItemReadOnly(item)}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * A domain group in the icon rail keeps its own icon above the item icons.
 * The group name is available to assistive technology and as a native tooltip.
 */
function CollapsedGroup({
  group,
  onNavigate,
  modules = ALL_MODULES_ACTIVE,
}: {
  group: NavGroup;
  onNavigate?: (() => void) | undefined;
  modules?: ModuleView;
}) {
  const { t } = useLocale();
  return (
    <ul
      role="list"
      aria-label={t(group.labelKey)}
      data-testid={`app-sidebar-group-${group.id}`}
      style={listStyle}
    >
      <li>
        <div
          role="heading"
          aria-level={2}
          aria-label={t(group.labelKey)}
          title={t(group.labelKey)}
          data-testid={`app-sidebar-group-${group.id}-icon`}
          style={collapsedGroupHeadingStyle}
        >
          <group.Icon />
        </div>
      </li>
      {group.items.map((item) => (
        <li key={item.id}>
          <SidebarItem
            item={item}
            collapsed
            onNavigate={onNavigate}
            readOnly={modules.isNavItemReadOnly(item)}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * A single nav item. Three honest variants:
 *   - `route` (default) — a `NavLink` with `aria-current` on the active route.
 *   - `preview` — a future capability with no route yet: a non-interactive, muted item
 *     with a "Soon" badge and a tooltip. No broken link, no placeholder page.
 *   - `external` — an out-link (new tab) for the Platform Admin Console.
 *
 * `collapsed` shows only the icon; the label moves to `aria-label` and `title`.
 */
function SidebarItem({
  item,
  collapsed = false,
  onNavigate,
  readOnly = false,
}: {
  item: NavItem;
  collapsed?: boolean;
  onNavigate?: (() => void) | undefined;
  /**
   * A-MODUL-NAV1 — das Modul dieses Punktes steht auf `read_only` (Paket
   * abgelaufen, 90-Tage-Lesefrist). Im Client ist das NUR eine Kennzeichnung:
   * Schloss-Symbol mit Text, Link bleibt begehbar. Die Schreibsperren macht
   * der Server (Lane C).
   */
  readOnly?: boolean;
}) {
  const { t } = useLocale();
  const label = t(item.labelKey);
  const readOnlyHint = readOnly ? t("nav.read_only.hint") : undefined;
  const description = item.descriptionKey ? t(item.descriptionKey) : undefined;
  const badge = !collapsed && item.badgeKey ? t(item.badgeKey) : undefined;
  const readOnlyBadge =
    readOnly && !collapsed ? (
      <ReadOnlyBadge label={t("nav.badge.read_only")} hint={readOnlyHint ?? ""} />
    ) : null;
  const testId = `app-sidebar-item-${item.id}`;
  // A-MODUL-NAV1 — die Kennzeichnung darf den Namen des Menuepunktes nicht
  // abschneiden. Deshalb darf die Zeile eines `read_only`-Punktes umbrechen und
  // der Name behaelt seine eigene Breite; passt die Pille nicht mehr daneben,
  // rutscht sie unter den Namen statt ihn zu kuerzen.
  const rowStyle = collapsed
    ? collapsedItemStyle
    : readOnly
      ? { ...itemStyle, flexWrap: "wrap" as const, rowGap: "var(--gt-sys-space-1)" }
      : itemStyle;
  const labelSlot = collapsed ? null : (
    <span style={readOnly ? readOnlyLabelSlotStyle : labelSlotStyle}>{label}</span>
  );

  if (item.kind === "preview") {
    return (
      <span
        data-testid={testId}
        aria-disabled="true"
        aria-label={collapsed ? label : undefined}
        title={description ?? t("nav.preview.hint")}
        style={{
          ...rowStyle,
          color: "var(--gt-sys-text-muted)",
          opacity: 0.65,
          cursor: "default",
        }}
      >
        <span aria-hidden style={iconSlotStyle}>
          <item.Icon />
        </span>
        {labelSlot}
        {badge ? <PreviewBadge label={badge} /> : null}
      </span>
    );
  }

  if (item.kind === "external") {
    // Paket 003707: the App Store / Google Play footer badges carry a full badge
    // graphic (`badgeImage`) instead of icon + text once expanded — the image
    // itself is the label, so it gets its own `aria-label` regardless of
    // `collapsed` (unlike the plain icon+text external items, where the visible
    // text already is the accessible name). Collapsed always falls back to the
    // small glyph + tooltip, same as every other item kind.
    const showBadgeImage = item.badgeImage && !collapsed;
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        aria-label={collapsed || item.badgeImage ? label : undefined}
        title={collapsed ? label : description}
        style={
          showBadgeImage
            ? { ...rowStyle, ...badgeAnchorStyle, color: "var(--gt-sys-text-muted)" }
            : { ...rowStyle, color: "var(--gt-sys-text-muted)" }
        }
      >
        {showBadgeImage ? (
          <img src={item.badgeImage?.src} alt={label} style={badgeImageStyle} />
        ) : (
          <>
            <span aria-hidden style={iconSlotStyle}>
              <item.Icon />
            </span>
            {labelSlot}
          </>
        )}
      </a>
    );
  }

  return (
    <NavLink
      to={item.to ?? "/"}
      end={item.exact ?? false}
      onClick={onNavigate}
      // E-PREFETCH-1 — siehe `routePrefetch.ts`: hoechstens ein Ladevorgang
      // je Pfad, und ein unbekannter Pfad tut schlicht nichts.
      onMouseEnter={() => prefetchRoute(item.to ?? "/")}
      onFocus={() => prefetchRoute(item.to ?? "/")}
      data-testid={testId}
      data-read-only={readOnly ? "true" : undefined}
      aria-label={collapsed ? label : undefined}
      title={
        collapsed
          ? [label, readOnlyHint].filter(Boolean).join(" — ")
          : (readOnlyHint ?? description)
      }
      style={({ isActive }) => ({
        ...rowStyle,
        // GT-GI-SHELL-FINAL-VISUAL-AND-AI-FIDELITY-FIX-01 (DL-125) — a clearer,
        // more premium active state: an accent-tinted pill + accent text/icon, so
        // the active route stands out from siblings and group headers.
        color: isActive ? "var(--gt-sys-accent)" : "var(--gt-sys-text-muted)",
        background: isActive
          ? "color-mix(in oklab, var(--gt-sys-accent) 12%, transparent)"
          : "transparent",
        fontWeight: isActive
          ? "var(--gt-sys-font-weight-semibold)"
          : "var(--gt-sys-font-weight-medium)",
      })}
    >
      <span aria-hidden style={iconSlotStyle}>
        <item.Icon />
      </span>
      {labelSlot}
      {readOnlyBadge}
      {badge ? <PreviewBadge label={badge} /> : null}
    </NavLink>
  );
}

/** A small accent "Soon" / "Preview" pill, pushed to the end of the item row. */
function PreviewBadge({ label }: { label: string }) {
  return <span style={badgeStyle}>{label}</span>;
}

/**
 * A-MODUL-NAV1 — Kennzeichnung "nur lesen": dieselbe Pille wie `PreviewBadge`,
 * nur in gedeckter Farbe und mit Schloss-Symbol VOR dem Text. Symbol allein
 * waere zu wenig (Owner-Lesbarkeit), deshalb Symbol plus Wort; `title` tragt
 * den vollen Satz fuer Zeigegeraete.
 */
function ReadOnlyBadge({ label, hint }: { label: string; hint: string }) {
  return (
    <span
      data-testid="app-sidebar-read-only-badge"
      title={hint}
      style={{
        ...badgeStyle,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "color-mix(in oklab, var(--gt-sys-text-muted) 12%, transparent)",
        color: "var(--gt-sys-text-muted)",
      }}
    >
      <LockIcon />
      {label}
    </span>
  );
}

/**
 * The collapse toggle's glyph: two chevrons pointing left while the sidebar is open
 * (click collapses) and, rotated 180°, right while it is the icon rail (click expands).
 */
function CollapseChevrons({ flipped }: { flipped: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        flexShrink: 0,
        transform: flipped ? "rotate(180deg)" : "none",
      }}
    >
      <path d="M11 17l-5-5 5-5" />
      <path d="M18 17l-5-5 5-5" />
    </svg>
  );
}

/**
 * Active-group detection: which domain group owns the current pathname (so it opens by
 * default). Query strings are stripped (e.g. `/intelligence?view=reports` → matches the
 * `/intelligence` path). Preview/external items (no `to`) never match.
 */
function pathMatchesItem(item: NavItem, pathname: string): boolean {
  if (!item.to) return false;
  // Paket 003707: the telegram item's `to` carries an in-app anchor
  // (`/me/notifications#telegram`) — strip both query and hash before
  // comparing against `pathname` (which never includes either).
  const path = item.to.split("?")[0]?.split("#")[0] ?? item.to;
  if (item.exact) return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

function activeGroupId(pathname: string): string | null {
  for (const group of NAV_GROUPS) {
    if (group.items.some((item) => pathMatchesItem(item, pathname))) return group.id;
  }
  return null;
}

// The nav fills the aside exactly (no growth past it), so brand and footer stay fixed.
const navStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--gt-sys-space-4)",
  inlineSize: "100%",
  blockSize: "100%",
  minBlockSize: 0,
  overflow: "hidden",
  paddingBlock: "var(--gt-sys-space-5)",
  paddingInline: "var(--gt-sys-space-4)",
};

// The icon rail keeps the same column rhythm but narrower side padding so the
// 72 px aside leaves room for the mark and the 36 px icon rows.
const navCollapsedStyle: CSSProperties = {
  ...navStyle,
  paddingInline: "var(--gt-sys-space-2)",
};

// The transparent logo sits directly on the sidebar surface in both themes.
const brandStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingBlock: "var(--gt-sys-space-3)",
  paddingInline: "var(--gt-sys-space-3)",
  color: "var(--gt-sys-text)",
  textDecoration: "none",
};

const groupToggleStyle: CSSProperties = {
  appearance: "none",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  font: "inherit",
  textAlign: "start",
  inlineSize: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "var(--gt-sys-space-2)",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-2)",
  color: "var(--gt-sys-text-muted)",
};

const groupLabelStyle: CSSProperties = {
  fontSize: "var(--gt-sys-font-size-xs)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
};

const groupHeadingStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-3)",
};

const chevronStyle: CSSProperties = {
  fontSize: 10,
  flexShrink: 0,
  color: "var(--gt-sys-text-muted)",
  transition: "transform var(--gt-ref-motion-duration-fast) var(--gt-ref-motion-ease-out)",
};

const scrollRegionStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--gt-sys-space-4)",
  flex: "1 1 auto",
  minBlockSize: 0,
  overflowY: "auto",
};

const listStyle: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

/**
 * GT-GI-SHELL-FINAL-VISUAL-AND-AI-FIDELITY-FIX-01 (DL-125) — domain-group children
 * are nested under a subtle rail + indent so the hierarchy reads at a glance:
 * the pinned cockpit items sit flush at the top level, while group children are
 * visibly nested beneath their (uppercase, tracked) group header.
 */
const groupListStyle: CSSProperties = {
  ...listStyle,
  marginInlineStart: "var(--gt-sys-space-2)",
  paddingInlineStart: "var(--gt-sys-space-3)",
  borderInlineStart: "1px solid var(--gt-sys-border)",
};

// Footer (Support …) is pushed to the bottom of the sidebar / drawer; the collapse
// toggle follows it as the very last row.
const footerStyle: CSSProperties = {
  marginBlockStart: "auto",
  paddingBlockStart: "var(--gt-sys-space-3)",
  borderBlockStart: "1px solid var(--gt-sys-border)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--gt-sys-space-2)",
};

const collapseToggleStyle: CSSProperties = {
  appearance: "none",
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-3)",
  inlineSize: "100%",
  paddingBlock: "var(--gt-sys-space-2)",
  paddingInline: "var(--gt-sys-space-2)",
  borderRadius: "var(--gt-sys-radius-md)",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  font: "inherit",
  fontSize: "var(--gt-sys-font-size-sm)",
  textAlign: "start",
  color: "var(--gt-sys-text-muted)",
  minBlockSize: 36,
};

const collapseToggleCollapsedStyle: CSSProperties = {
  ...collapseToggleStyle,
  justifyContent: "center",
  paddingInline: 0,
};

const itemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-3)",
  paddingBlock: "var(--gt-sys-space-2)",
  paddingInline: "var(--gt-sys-space-2)",
  borderRadius: "var(--gt-sys-radius-md)",
  fontSize: "var(--gt-sys-font-size-sm)",
  textDecoration: "none",
  minBlockSize: 36,
};

const collapsedItemStyle: CSSProperties = {
  ...itemStyle,
  justifyContent: "center",
  gap: 0,
};

const collapsedGroupHeadingStyle: CSSProperties = {
  ...collapsedItemStyle,
  color: "var(--gt-sys-text-muted)",
};

const iconSlotStyle: CSSProperties = { display: "inline-flex", flexShrink: 0 };

// Paket 004060 (B36) — the App Store / Google Play footer badges: both badges
// share one spacing-token row height and each badge
// slot grows to fill its half of the row (together exactly the sidebar's inner
// width, never more); `objectFit: contain` keeps each badge's own aspect ratio
// instead of stretching it to match the other's.
const badgeImageStyle: CSSProperties = {
  display: "block",
  height: "100%",
  width: "100%",
  objectFit: "contain",
};

// Lets the badge `<a>` (itself `display: flex`) stretch across its slot and the
// full row height instead of shrinking to the image's intrinsic size / the
// default nav-row padding and minimum height.
const badgeAnchorStyle: CSSProperties = {
  inlineSize: "100%",
  blockSize: "100%",
  minBlockSize: 0,
  paddingBlock: 0,
  paddingInline: 0,
  justifyContent: "center",
};

const storeBadgeRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "stretch",
  gap: "var(--gt-sys-space-2)",
  blockSize: "var(--gt-sys-space-6)",
};

const storeBadgeSlotStyle: CSSProperties = { display: "flex", flex: "1 1 0", minWidth: 0 };

const labelSlotStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/**
 * A-MODUL-NAV1 — Namensfeld eines gekennzeichneten Punktes: eigene Breite
 * (`flexBasis: auto`) statt der sonst ueblichen Null-Basis, damit die Pille
 * umbricht und nicht der Name gekuerzt wird.
 */
const readOnlyLabelSlotStyle: CSSProperties = {
  ...labelSlotStyle,
  flex: "1 1 auto",
};

const badgeStyle: CSSProperties = {
  flexShrink: 0,
  marginInlineStart: "var(--gt-sys-space-2)",
  paddingInline: "var(--gt-sys-space-2)",
  paddingBlock: 1,
  borderRadius: "var(--gt-sys-radius-pill, 999px)",
  background: "color-mix(in oklab, var(--gt-sys-accent) 12%, transparent)",
  color: "var(--gt-sys-accent)",
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-medium)",
  lineHeight: 1.4,
};
